import React, { useState, useRef, useEffect } from 'react';
import { CameraService } from '../service/camera.service';
import { TextField, Typography, Paper, Button, IconButton, Box, Switch, Grid, Tooltip, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VideocamIcon from '@mui/icons-material/Videocam';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';

interface Kamera {
  ref: string; // Kamera-Referenz
  displayName: string;
  url: string;
  abstandsschwelle: number; // Abstandsschwelle in Float (z. B. 0.05 bis 0.2)
  websocketOff: boolean;
}

interface KameraItemProps {
  kamera: Kamera;
  onUpdate: (data: Partial<Kamera>) => void;
  onDelete: () => void;
}

// URL-Komponenten Interface
interface UrlComponents {
  protocol: string;
  username: string;
  password: string;
  ip: string;
  port: string;
  path: string;
}

// Funktion zum Parsen einer URL in ihre Komponenten
const parseUrl = (url: string): UrlComponents => {
  const components: UrlComponents = {
    protocol: 'rtsp',
    username: '',
    password: '',
    ip: '',
    port: '554',
    path: ''
  };

  try {
    // Extrahiere Protokoll
    const protocolMatch = url.match(/^([a-z]+):\/\//i);
    if (protocolMatch) {
      components.protocol = protocolMatch[1];
    }

    // Extrahiere Authentifizierungsdaten und Host/IP
    const authAndHostMatch = url.match(/\/\/(?:([^:@]+):([^@]+)@)?([^:/]+)(?::(\d+))?/);
    if (authAndHostMatch) {
      if (authAndHostMatch[1]) components.username = authAndHostMatch[1];
      if (authAndHostMatch[2]) components.password = authAndHostMatch[2];
      if (authAndHostMatch[3]) components.ip = authAndHostMatch[3];
      if (authAndHostMatch[4]) components.port = authAndHostMatch[4];
    }

    // Extrahiere Pfad
    const pathMatch = url.match(/:\d+(?:\/(.*))?$/);
    if (pathMatch && pathMatch[1]) {
      components.path = pathMatch[1];
    } else {
      // Alternativ, wenn kein Port angegeben ist
      const altPathMatch = url.match(/[^/]+\/(.*)$/);
      if (altPathMatch) {
        components.path = altPathMatch[1];
      }
    }

    return components;
  } catch (error) {
    console.error('Fehler beim Parsen der URL:', error);
    return components;
  }
};

// Funktion zum Erstellen einer URL aus Komponenten
const buildUrl = (components: UrlComponents): string => {
  let url = `${components.protocol}://`;
  
  if (components.username && components.password) {
    url += `${components.username}:${components.password}@`;
  }
  
  url += components.ip;
  
  if (components.port) {
    url += `:${components.port}`;
  }
  
  if (components.path) {
    // Stellen Sie sicher, dass der Pfad mit einem Schrägstrich beginnt
    if (!components.path.startsWith('/')) {
      url += '/';
    }
    url += components.path;
  }
  
  return url;
};

const KameraItem: React.FC<KameraItemProps> = ({ kamera, onUpdate, onDelete }) => {
  const [editingDisplayName, setEditingDisplayName] = useState<string | null>(null);
  const [editingUrl, setEditingUrl] = useState<string | null>(null);
  const [editingUrlComponents, setEditingUrlComponents] = useState<UrlComponents | null>(null);
  const [editingAbstandsschwelle, setEditingAbstandsschwelle] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const isUpdating = useRef<boolean>(false);
  
  // IP und Port aus der URL extrahieren
  const urlComponents = parseUrl(kamera.url);

  const handleDisplayNameSubmit = async () => {
    if (editingDisplayName) {
      try {
        onUpdate({ displayName: editingDisplayName });
        setEditingDisplayName(null);
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Anzeigenamens:', error);
        alert('Fehler beim Aktualisieren des Anzeigenamens.');
      }
    }
  };

  const handleToggleWebsocket = async () => {
    if (isUpdating.current) {
      return;
    }
    
    const newWebsocketOff = !kamera.websocketOff;
    
    isUpdating.current = true;
    
    try {
      console.log(`Websocket-Status wird aktualisiert auf: ${newWebsocketOff ? 'Aus' : 'Ein'}`);
      onUpdate({ websocketOff: newWebsocketOff });
      
    } catch (error) {
      console.error('Fehler beim Aktualisieren von websocketOff:', error);
      // Bei Fehler den ursprünglichen Status wiederherstellen
      onUpdate({ websocketOff: kamera.websocketOff });
      alert('Fehler beim Aktualisieren von WebSocket.');
    } finally {
      // Setze die Flag zurück mit einer kleinen Verzögerung, um doppelte Klicks zu verhindern
      setTimeout(() => {
        isUpdating.current = false;
      }, 500);
    }
  };
  
  // Starte die URL-Bearbeitung mit den geparsten Komponenten
  const startUrlEditing = () => {
    const components = parseUrl(kamera.url);
    setEditingUrlComponents(components);
    setEditingUrl(kamera.url);
  };

  // Aktualisiere die URL-Komponenten, wenn ein Feld geändert wird
  const handleComponentChange = (field: keyof UrlComponents, value: string) => {
    if (editingUrlComponents) {
      const newComponents = { ...editingUrlComponents, [field]: value };
      setEditingUrlComponents(newComponents);
      setEditingUrl(buildUrl(newComponents));
    }
  };

  const handleUrlSubmit = async () => {
    if (editingUrl) {
      try {
        onUpdate({ url: editingUrl });
        setEditingUrl(null);
        setEditingUrlComponents(null);
      } catch (error) {
        console.error('Fehler beim Aktualisieren der URL:', error);
        alert('Fehler beim Aktualisieren der URL.');
      }
    }
  };

  const handleAbstandsschwelleSubmit = async () => {
    if (editingAbstandsschwelle) {
      // Convert percentage to decimal (e.g., 15% -> 0.15)
      const parsed = parseFloat(editingAbstandsschwelle) / 100;
      if (parsed < 0.05 || parsed > 0.2) {
        alert('Bitte einen Wert zwischen 5% und 20% eingeben.');
        return;
      }
      try {
        onUpdate({ abstandsschwelle: parsed });
        setEditingAbstandsschwelle(null);
      } catch (error) {
        console.error('Fehler beim Aktualisieren der Abstandsschwelle:', error);
        alert('Fehler beim Aktualisieren der Abstandsschwelle.');
      }
    }
  };

  const handleDelete = async () => {
    try {
      console.log('Löschvorgang für Kamera eingeleitet:', kamera.ref);
      onDelete();
    } catch (error) {
      console.error('Fehler beim Löschen der Kamera:', error);
      alert('Fehler beim Löschen der Kamera.');
    }
  };

  return (
    <Paper sx={{ 
      padding: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.08)'
      }
    }}>
      {/* Anzeigename bearbeiten */}
      {editingDisplayName !== null ? (
        <Box display="flex" alignItems="center" gap="10px" mb={2}>
          <TextField
            value={editingDisplayName}
            onChange={(e) => setEditingDisplayName(e.target.value)}
            placeholder="Neuer Name"
            variant="outlined"
            size="small"
            fullWidth
          />
          <Button variant="contained" onClick={handleDisplayNameSubmit} size="small">
            Bestätigen
          </Button>
          <Button variant="outlined" onClick={() => setEditingDisplayName(null)} size="small">
            Abbrechen
          </Button>
        </Box>
      ) : (
        <>
          {/* Header mit Kameraname */}
          <Box display="flex" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <VideocamIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">{kamera.displayName}</Typography>
              <IconButton 
                onClick={() => setEditingDisplayName(kamera.displayName)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          {/* Kamerainformationen */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" color="text.secondary">IP-Adresse</Typography>
                <Typography variant="body1">{urlComponents.ip}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" color="text.secondary">Port</Typography>
                <Typography variant="body1">{urlComponents.port}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Auslösen:</Typography>
                <Switch
                  checked={!kamera.websocketOff}
                  onChange={handleToggleWebsocket}
                  color="primary"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Abstandsschwelle:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {(kamera.abstandsschwelle * 100).toFixed(0)}%
                </Typography>
                <Tooltip title="Der Abstand zwischen erkannten Autos in Prozent der Bildbreite. Ein höherer Wert bedeutet, dass Autos weiter voneinander entfernt sein müssen, um einen Not-Stop auszulösen." arrow placement="top">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          {/* Aktionsbuttons */}
          <Box display="flex" gap={2} sx={{ mt: 3 }}>
            <Button 
              variant="contained" 
              onClick={startUrlEditing}
              size="small"
              startIcon={<SettingsIcon />}
            >
              Kamera bearbeiten
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setEditingAbstandsschwelle((kamera.abstandsschwelle * 100).toFixed(0))}
              size="small"
            >
              Abstandsschwelle ändern
            </Button>
            <Button 
              variant="outlined" 
              color="error"
              onClick={() => setConfirmDelete(true)}
              size="small"
              sx={{ ml: 'auto' }}
            >
              Löschen
            </Button>
          </Box>
        </>
      )}

      {/* Bearbeiten der URL mit erweitertem Formular */}
      {editingUrlComponents !== null && (
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" gutterBottom>URL Einstellungen bearbeiten</Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                value={editingUrlComponents.ip}
                onChange={(e) => handleComponentChange('ip', e.target.value)}
                label="Kamera IP Adresse"
                variant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={editingUrlComponents.port}
                onChange={(e) => handleComponentChange('port', e.target.value)}
                label="Port"
                variant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={editingUrlComponents.username}
                onChange={(e) => handleComponentChange('username', e.target.value)}
                label="Benutzername"
                variant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={editingUrlComponents.password}
                onChange={(e) => handleComponentChange('password', e.target.value)}
                label="Passwort"
                type="password"
                variant="outlined"
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
          <Box display="flex" gap={2}>
            <Button variant="contained" onClick={handleUrlSubmit} size="small">
              Bestätigen
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => {
                setEditingUrl(null);
                setEditingUrlComponents(null);
              }} 
              size="small"
            >
              Abbrechen
            </Button>
          </Box>
        </Box>
      )}

      {/* Bearbeiten der Abstandsschwelle */}
      {editingAbstandsschwelle !== null && (
        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle1" gutterBottom>Abstandsschwelle ändern</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              value={editingAbstandsschwelle}
              onChange={(e) => setEditingAbstandsschwelle(e.target.value)}
              label="Abstandsschwelle (5% - 20%)"
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ step: "1", min: "5", max: "20" }}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="body2">%</Typography>
              }}
            />
            <Box display="flex" gap={2} mt={1}>
              <Button variant="contained" onClick={handleAbstandsschwelleSubmit} size="small">
                Bestätigen
              </Button>
              <Button variant="outlined" onClick={() => setEditingAbstandsschwelle(null)} size="small">
                Abbrechen
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Bestätigen oder Abbrechen des Löschens */}
      {confirmDelete && (
        <Box 
          sx={{ 
            mt: 3, 
            pt: 3, 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body1" fontWeight="medium" color="error.main">
            Kamera wirklich löschen?
          </Typography>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            size="small"
          >
            Löschen
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setConfirmDelete(false)}
            size="small"
          >
            Abbrechen
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default KameraItem;
